import { memo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { getPageContainerClasses } from "@/utils/layout";

interface GenericPageSkeletonProps {
  showMetrics?: boolean;
  contentCards?: number;
  variant?: 'standard' | 'compact';
}

/**
 * Modern page skeleton with gradient backgrounds and soft styling
 */
export const GenericPageSkeleton = memo(({ 
  showMetrics = false, 
  contentCards = 3,
  variant = 'standard'
}: GenericPageSkeletonProps) => {
  const spacing = variant === 'compact' ? 'space-y-4' : 'space-y-6';
  
  return (
    <div className={getPageContainerClasses()}>
      <div className={spacing}>
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap opacity-0 animate-[fade-in_0.3s_ease-out_0.1s_forwards]">
          <div className="space-y-3 flex-1 min-w-0">
            <Skeleton variant="default" className="h-9 w-56 max-w-full" />
            <Skeleton variant="soft" className="h-5 w-80 max-w-full" />
          </div>
          <Skeleton variant="accent" className="h-11 w-36" />
        </div>

        {/* Optional metrics row */}
        {showMetrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div 
                key={i}
                className="p-5 rounded-2xl shadow-sm backdrop-blur-sm bg-gradient-to-br from-card/50 to-muted/20 opacity-0 animate-[fade-in_0.3s_ease-out_forwards]"
                style={{ animationDelay: `${0.2 + i * 0.05}s` }}
              >
                <div className="space-y-2">
                  <Skeleton variant="soft" className="h-4 w-24" />
                  <Skeleton variant="accent" className="h-8 w-16" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Content cards */}
        <div className={spacing}>
          {Array.from({ length: contentCards }).map((_, i) => (
            <div 
              key={i}
              className="p-6 rounded-2xl shadow-sm backdrop-blur-sm bg-gradient-to-br from-card/50 to-muted/20 opacity-0 animate-[fade-in_0.3s_ease-out_forwards]"
              style={{ animationDelay: `${0.3 + i * 0.1}s` }}
            >
              <div className="space-y-4">
                <Skeleton variant="accent" className="h-7 w-48" />
                <Skeleton variant="default" className="h-4 w-full" />
                <Skeleton variant="default" className="h-4 w-[85%]" />
                <Skeleton variant="soft" className="h-4 w-[60%]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

GenericPageSkeleton.displayName = "GenericPageSkeleton";
